/**
 * ReportsTable Component
 *
 * The reports list table with its toolbar (rows per page, filters, view and
 * column selector) and pagination row. Mirrors JournalArticlesTable without
 * the article-only parts (collections, topics, PR score, location columns, and
 * the remove-from-collection column). The Year column is always sortable.
 */

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"
import { JournalArticle, extractionStatusLabelMap, formatInstitutions } from "@/services/journalArticlesService"
import { getReportTypeLabel } from "@/services/reportsService"
import ExtractStatusBadge from "@/components/journal-articles/ExtractStatusBadge"
import { PAGE_SIZE_OPTIONS } from "@/components/journal-articles/useJournalArticlesPage"
import LoadingSpinner from "@/components/shared/LoadingSpinner"
import SortableColumnHeader from "@/components/survey-catalogue/SortableColumnHeader"
import TableFiltersSelector, { FilterOption } from "@/components/shared/TableFiltersSelector"
import TableViewSelector, { ViewMode } from "@/components/shared/TableViewSelector"
import { TablePageSizeSelector } from "@/components/shared/TablePageSizeSelector"
import { TruncatedCell } from "@/components/shared/TruncatedCell"
import { getColumnGroupsForViewMode } from "@/components/reports/columns"
import { ReportsPage } from "@/components/reports/useReportsPage"
import { formatDate } from "@/utils/dateUtils"
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react"

/** Props for the ReportsTable component. */
interface ReportsTableProps {
  /** The page object from useReportsPage (list state, sort, handlers). */
  page: ReportsPage
  /** The filter options offered in the toolbar's filter selector. */
  filterOptions: FilterOption[]
  /** The set of visible column keys. */
  visibleColumns: Set<string>
}

/**
 * The view modes the reports table offers. "pr-bulk-review" is article-only;
 * getColumnGroupsForViewMode throws for it.
 */
const VIEW_MODE_OPTIONS: ViewMode[] = ["compact", "status", "full"]

/** Columns that are always visible regardless of the column selector. */
const TOOLBAR_FIXED_COLUMNS = new Set<keyof JournalArticle>(["sourceId", "title"])

/**
 * Build the filter options offered on every reports page: the PR and RS
 * extraction status filters. The keys match what buildReportsFilters in
 * ReportsContext accepts; the options are the extractionStatusLabelMap labels,
 * which buildReportsFilters maps back to API values.
 *
 * @returns The two status filter options.
 */
export function buildReportFilterOptions(): FilterOption[] {
  return [
    {
      key: "prExtractionStatus",
      label: "PR Extraction Status",
      options: Object.values(extractionStatusLabelMap),
    },
    {
      key: "rsExtractionStatus",
      label: "RS Extraction Status",
      options: Object.values(extractionStatusLabelMap),
    },
  ]
}

/** Column definition for the reports table. */
interface ReportTableColumn {
  /** The column key (a JournalArticle field name, as in reports/columns.ts). */
  key: string
  /** Header label text. */
  label: string
  /** Minimum width CSS class. */
  minWidth: string
}

/**
 * Every column the reports table can render, in display order (the same order
 * as the full view's column groups). Visibility is decided by visibleColumns.
 */
const REPORT_TABLE_COLUMNS: ReportTableColumn[] = [
  { key: "sourceId", label: "Source ID", minWidth: "min-w-[90px]" },
  { key: "title", label: "Title", minWidth: "min-w-[450px]" },
  { key: "reportType", label: "Report Type", minWidth: "min-w-[150px]" },
  { key: "authors", label: "Authors", minWidth: "min-w-[200px]" },
  { key: "publicationYear", label: "Year", minWidth: "min-w-[60px]" },
  { key: "pages", label: "Pages", minWidth: "min-w-[80px]" },
  { key: "institutions", label: "Institutions", minWidth: "min-w-[150px]" },
  { key: "reportNumber", label: "Report Number", minWidth: "min-w-[100px]" },
  { key: "ihmeNid", label: "IHME NID", minWidth: "min-w-[100px]" },
  { key: "mapSurveyId", label: "MAP Survey ID", minWidth: "min-w-[100px]" },
  { key: "surveyType", label: "Survey Type", minWidth: "min-w-[100px]" },
  { key: "citation", label: "Citation", minWidth: "min-w-[200px]" },
  { key: "suggestedCitation", label: "Suggested Citation", minWidth: "min-w-[200px]" },
  { key: "url", label: "URL", minWidth: "min-w-[120px]" },
  { key: "city", label: "City", minWidth: "min-w-[100px]" },
  { key: "researchNotes", label: "Research Notes", minWidth: "min-w-[150px]" },
  { key: "usagePermission", label: "Usage Permission", minWidth: "min-w-[100px]" },
  { key: "dateUrlVisited", label: "Date URL Visited", minWidth: "min-w-[100px]" },
  { key: "createdBy", label: "Created By", minWidth: "min-w-[100px]" },
  { key: "createdDate", label: "Created Date", minWidth: "min-w-[100px]" },
  { key: "lastModifiedBy", label: "Last Modified By", minWidth: "min-w-[100px]" },
  { key: "lastModifiedDate", label: "Last Modified Date", minWidth: "min-w-[100px]" },
  { key: "prExtractionStatus", label: "PR Extraction Status", minWidth: "min-w-[160px]" },
  { key: "rsExtractionStatus", label: "RS Extraction Status", minWidth: "min-w-[160px]" },
]

/** Keys whose cells get the narrower max width (long free text). */
const CLAMP_KEYS = new Set<string>(["authors", "citation", "suggestedCitation", "researchNotes"])

/**
 * Render a text value through TruncatedCell, or "—" when empty.
 *
 * @param value - The text to show.
 * @returns The truncating cell, or a dash.
 */
function renderTruncated(value: string | null | undefined): React.ReactNode {
  return value != null && value !== "" ? <TruncatedCell value={value} /> : "—"
}

/**
 * Render one cell of a report row.
 *
 * @param report - The report the row shows.
 * @param key - The column key.
 * @returns The cell content.
 * @throws Error (from getReportTypeLabel) when the report carries a reportType
 *   missing from reportTypeLabelMap.
 */
function renderCellValue(report: JournalArticle, key: string): React.ReactNode {
  switch (key) {
    case "sourceId":
      return report.sourceId
    case "title":
      return <span className="whitespace-normal cursor-default">{report.title}</span>
    case "reportType":
      return <Badge variant="outline">{getReportTypeLabel(report.reportType)}</Badge>
    case "publicationYear":
      return report.publicationYear != null ? report.publicationYear : "—"
    case "institutions":
      // Reports store institutions as a list; formatInstitutions joins it.
      return renderTruncated(formatInstitutions(report.institutions))
    case "authors":
      return renderTruncated(report.authors?.map((a) => a.name).join("; "))
    case "prExtractionStatus":
      return report.prExtractionStatus ? <ExtractStatusBadge status={report.prExtractionStatus} /> : "—"
    case "rsExtractionStatus":
      return report.rsExtractionStatus ? <ExtractStatusBadge status={report.rsExtractionStatus} /> : "—"
    case "createdDate":
      return formatDate(report.createdDate)
    case "lastModifiedDate":
      return formatDate(report.lastModifiedDate)
    case "url":
      if (!report.url) return "—"
      return (
        <a
          href={report.url}
          target="_blank"
          rel="noopener noreferrer"
          title={report.url}
          // Stop propagation so following the link does not also open the details panel.
          onClick={(e) => e.stopPropagation()}
          className="block truncate text-primary underline underline-offset-2"
        >
          {report.url}
        </a>
      )
    default: {
      // Remaining columns are plain scalar fields on JournalArticle.
      const value: unknown = report[key as keyof JournalArticle]
      return renderTruncated(value != null ? String(value) : null)
    }
  }
}

/**
 * Reports table with toolbar and pagination.
 *
 * Clicking a row selects that report (opening the details panel) and
 * highlights the row.
 *
 * @param props - Component props.
 * @param props.page - The page object from useReportsPage.
 * @param props.filterOptions - Filter options for the toolbar's filter selector.
 * @param props.visibleColumns - The set of visible column keys.
 * @returns The rendered table.
 */
export default function ReportsTable({
  page,
  filterOptions,
  visibleColumns,
}: ReportsTableProps): React.JSX.Element {
  const {
    reports,
    loading,
    error,
    sortConfig,
    toggleSort,
    selectReport,
    selectedReportId,
    totalCount,
    pageIndex,
    pageSize,
    totalPages,
    activeFilters,
    viewMode,
    setViewMode,
    setVisibleColumns,
    changePageSize,
    changeFilters,
    goToFirstPage,
    goToPreviousPage,
    goToNextPage,
    goToLastPage,
  } = page

  // The 1-based inclusive range of the current page, shown in the summary.
  const fromItem: number = pageIndex * pageSize + 1
  const toItem: number = Math.min((pageIndex + 1) * pageSize, totalCount)

  const activeColumns: ReportTableColumn[] = REPORT_TABLE_COLUMNS.filter((col) => visibleColumns.has(col.key))
  const columnCount: number = activeColumns.length || 1

  return (
    <div className="flex flex-1 flex-col min-h-0">
      {/* Pagination summary + toolbar row, directly above the table. */}
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        {/* Left: report-count summary + page navigation. */}
        <div className="flex gap-1 flex-wrap">
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {loading ? (
              <div className="flex align-middle gap-2">
                <Spinner className="text-primary" />
                Loading...
              </div>
            ) : totalCount > 0 ? (
              `${fromItem.toLocaleString()}–${toItem.toLocaleString()} of ${totalCount.toLocaleString()} reports`
            ) : (
              "No reports"
            )}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="xs"
              className="p-0 ml-2 -mr-1"
              onClick={goToFirstPage}
              disabled={pageIndex === 0 || loading}
              aria-label="Go to first page"
            >
              <ChevronsLeft className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="xs"
              className="p-1 mr-1"
              onClick={goToPreviousPage}
              disabled={pageIndex === 0 || loading}
              aria-label="Go to previous page"
            >
              <ChevronLeft className="size-4" /> Prev
            </Button>
            <Button
              variant="ghost"
              size="xs"
              className="p-1"
              onClick={goToNextPage}
              disabled={pageIndex >= totalPages - 1 || loading}
              aria-label="Go to next page"
            >
              Next <ChevronRight className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="xs"
              className="p-0 -ml-1"
              onClick={goToLastPage}
              disabled={pageIndex >= totalPages - 1 || loading}
              aria-label="Go to last page"
            >
              <ChevronsRight className="size-4" />
            </Button>
          </div>
        </div>

        {/* Right: rows-per-page, filters, view selector. */}
        <div className="flex items-center gap-2 flex-wrap">
          <TablePageSizeSelector
            size={String(pageSize)}
            onChange={changePageSize}
            options={PAGE_SIZE_OPTIONS}
          />
          <TableFiltersSelector
            filterOptions={filterOptions}
            onSave={changeFilters}
            initialFilters={activeFilters}
          />
          <TableViewSelector
            value={viewMode}
            onChange={setViewMode}
            options={VIEW_MODE_OPTIONS}
            columnGroups={getColumnGroupsForViewMode(viewMode)}
            visibleKeys={visibleColumns}
            fixedColumns={TOOLBAR_FIXED_COLUMNS}
            onSaveColumns={setVisibleColumns}
          />
        </div>
      </div>

      {/* The scrollable table itself. */}
      <div className="flex-1 min-h-0 overflow-auto border border-border/50">
        <Table className={loading ? "h-full" : ""}>
          <TableHeader className="sticky top-0 z-10 bg-card shadow-sm">
            <TableRow>
              {activeColumns.map((col) => (
                <TableHead key={col.key} className={col.minWidth}>
                  {col.key === "publicationYear" ? (
                    <SortableColumnHeader
                      columnKey="publicationYear"
                      label={col.label}
                      sortConfig={sortConfig}
                      onToggleSort={toggleSort}
                    />
                  ) : (
                    col.label
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {error !== null ? (
              <TableRow>
                <TableCell colSpan={columnCount} className="text-center text-destructive py-8">
                  Error loading reports: {error}
                </TableCell>
              </TableRow>
            ) : loading ? (
              <TableRow>
                <TableCell colSpan={columnCount}>
                  <LoadingSpinner
                    loadingContent="Loading reports..."
                    width="w-[calc(100vw-var(--sidebar-width)-var(--secondary-sidebar-width))]"
                  />
                </TableCell>
              </TableRow>
            ) : reports.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columnCount} className="text-center text-muted-foreground py-8">
                  No reports found.
                </TableCell>
              </TableRow>
            ) : (
              reports.map((report) => (
                <TableRow
                  key={report.sourceId}
                  className={`hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-colors cursor-pointer ${report.sourceId === selectedReportId ? "bg-blue-50 dark:bg-blue-950/30" : ""}`}
                  onClick={() => selectReport(report)}
                  aria-selected={report.sourceId === selectedReportId}
                >
                  {activeColumns.map((col) => (
                    <TableCell
                      key={col.key}
                      className={CLAMP_KEYS.has(col.key) ? "max-w-[250px]" : "max-w-[300px]"}
                    >
                      {renderCellValue(report, col.key)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
